import api from '../../../services/api';
import { normalizeCompanyCostGroup, type CompanyCostGroup } from '../../companyCosts/costGroups';

export interface BudgetMoney {
  revenue: number;
  totalCost: number;
  margin: number;
  marginPercent: number;
}

/**
 * The plan of a month or of the year (decision 2026-10-29-grupy-kosztow-i-budzet):
 * revenue, margin plan (null = not set), fixed-cost plan split into cost
 * groups and „Bez grupy”, plan EBITDA (margin − fixed costs, null without a
 * margin plan), the entered net profit target and the profit computed from
 * the plan (EBITDA − 9% CIT year to date). Computed by the backend.
 */
export interface BudgetPlan {
  hasPlan: boolean;
  revenue: number;
  margin: number | null;
  marginPercent: number | null;
  fixedCosts: number;
  groups: { groupId: string; amount: number }[];
  ungrouped: number;
  ebitda: number | null;
  netProfit: number | null;
  netProfitCalculated: number | null;
}

export interface CompanyBudgetMonth {
  month: string;
  budget: BudgetPlan;
  forecast: BudgetMoney;
  actual: BudgetMoney;
  fixedCosts: number;
  // Net profit target of the month (budget); null = not planned.
  netProfitTarget: number | null;
  orderCount: number;
  plannedOrderCount: number;
  reviewOrderCount: number;
  note: string | null;
  revisionId: string | null;
}

export interface CompanyBudgetWorkspaceDto {
  year: number;
  /** Every cost group (active and archived), for names and the plan editor rows. */
  groups: CompanyCostGroup[];
  totals: { budget: BudgetPlan; forecast: BudgetMoney; actual: BudgetMoney };
  // Sum of the planned months' net profit targets; null when no month has one.
  netProfitTarget: number | null;
  months: CompanyBudgetMonth[];
}

/**
 * One month of PUT /company-budgets/years/:year. A field left out keeps the
 * month's saved value; `groupCostTargets` replaces the month's group plans.
 */
export interface CompanyBudgetYearMonthInput {
  month: string;
  revenueTarget?: number;
  marginTarget?: number | null;
  netProfitTarget?: number | null;
  groupCostTargets?: { groupId: string; amount: number }[];
  ungroupedCostTarget?: number;
}

const record = (value: unknown): Record<string, any> => value && typeof value === 'object' ? value as Record<string, any> : {};
const num = (value: unknown): number => Number.isFinite(Number(value)) ? Number(value) : 0;
const text = (value: unknown): string | null => typeof value === 'string' && value ? value : null;
const nullableNum = (value: unknown): number | null => (value == null || value === '' || !Number.isFinite(Number(value)) ? null : Number(value));
const plan = (value: unknown): BudgetPlan => {
  const source = record(value);
  return {
    hasPlan: source.hasPlan === true,
    revenue: num(source.revenue),
    margin: nullableNum(source.margin),
    marginPercent: nullableNum(source.marginPercent),
    fixedCosts: num(source.fixedCosts ?? source.totalCost),
    groups: Array.isArray(source.groups)
      ? source.groups.map(record).filter((group) => typeof group.groupId === 'string').map((group) => ({ groupId: group.groupId as string, amount: num(group.amount) }))
      : [],
    ungrouped: num(source.ungrouped),
    ebitda: nullableNum(source.ebitda),
    netProfit: nullableNum(source.netProfit),
    netProfitCalculated: nullableNum(source.netProfitCalculated),
  };
};
const money = (value: unknown): BudgetMoney => {
  const source = record(value);
  return { revenue: num(source.revenue), totalCost: num(source.totalCost), margin: num(source.margin), marginPercent: num(source.marginPercent) };
};

export function normalizeCompanyBudgetWorkspace(value: unknown): CompanyBudgetWorkspaceDto {
  const source = record(value);
  const totals = record(source.totals);
  return {
    year: num(source.year),
    groups: Array.isArray(source.groups)
      ? source.groups.map(normalizeCompanyCostGroup).filter((group): group is CompanyCostGroup => group !== null)
      : [],
    totals: { budget: plan(totals.budget), forecast: money(totals.forecast), actual: money(totals.actual) },
    netProfitTarget: nullableNum(record(totals.budget).netProfit),
    months: Array.isArray(source.months) ? source.months.map((item) => {
      const month = record(item);
      return {
        month: text(month.month) || '', budget: plan(month.budget), forecast: money(month.forecast), actual: money(month.actual),
        fixedCosts: num(month.fixedCosts), netProfitTarget: nullableNum(record(month.budget).netProfit), orderCount: num(month.orderCount), plannedOrderCount: num(month.plannedOrderCount),
        reviewOrderCount: num(month.reviewOrderCount), note: text(month.note), revisionId: text(month.revisionId),
      };
    }) : [],
  };
}

export async function fetchCompanyBudgetWorkspace(year: number): Promise<CompanyBudgetWorkspaceDto> {
  const response = await api.get('/company-budgets/workspace', { params: { year } });
  return normalizeCompanyBudgetWorkspace(response.data);
}

export async function saveCompanyBudgetMonth(month: string, input: { revenueTarget: number; totalCostTarget: number; note?: string | null }): Promise<void> {
  await api.put(`/company-budgets/months/${encodeURIComponent(month)}`, input);
}

export async function clearCompanyBudgetMonth(month: string): Promise<void> {
  await api.delete(`/company-budgets/months/${encodeURIComponent(month)}`);
}

// Writes the yearly plan (one budget revision per month, one transaction).
// The backend keeps each month's cost plan when only the targets change.
export async function saveCompanyBudgetYear(year: number, months: CompanyBudgetYearMonthInput[]): Promise<void> {
  await api.put(`/company-budgets/years/${year}`, { months });
}
